import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaLinkedin, FaGoogle, FaEnvelope, FaBriefcase, FaGraduationCap, FaArrowLeft, FaEdit, FaTrash, FaBuilding, FaFlask, FaTrophy, FaFilePdf, FaMapMarkerAlt, FaPen } from 'react-icons/fa';
import { parseEducation } from '../utils/education';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { generateAlumniCV } from '../utils/generateCV';
import usePageTitle from '../hooks/usePageTitle';

const AlumniDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t } = useLanguage();
  const [alumni, setAlumni] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPendingEdit, setHasPendingEdit] = useState(false);
  usePageTitle(alumni ? alumni.name : null);

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await supabase.from('alumni').select('*').eq('id', parseInt(id, 10)).single();
      setAlumni(data || null);

      // Check if there's a pending edit request for this alumni
      const { data: pendingReqs } = await supabase
        .from('alumni_edit_requests')
        .select('id')
        .eq('alumni_id', parseInt(id, 10))
        .eq('status', 'pending')
        .limit(1);
      setHasPendingEdit(pendingReqs && pendingReqs.length > 0);

      setLoading(false);
    };
    fetchData();
  }, [id]);

  const handleDelete = () => {
    if (!window.confirm(t('detail.confirmDelete'))) return;
    supabase.from('alumni').delete().eq('id', parseInt(id, 10))
      .then(({ error }) => {
        if (error) { alert('Gagal menghapus'); return; }
        navigate('/alumni');
      })
      .catch(() => alert('Gagal menghapus'));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!alumni) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h2 className="text-2xl font-bold text-gray-700 mb-2">{t('detail.notFound')}</h2>
        <p className="text-gray-500 mb-6">{t('detail.notFoundDesc')}</p>
        <Link to="/alumni" className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-2">
          <FaArrowLeft /> {t('detail.backToDir')}
        </Link>
      </div>
    );
  }

  const edu = parseEducation(alumni.education);
  const eduLine = (kampus, jurusan) => {
    if (kampus && jurusan) return `${kampus} — ${jurusan}`;
    return kampus || jurusan || '';
  };

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header gradient */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 h-48 md:h-56"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Profile header */}
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 border-b border-gray-100">
            {alumni.photo_url ? (
              <img src={alumni.photo_url} alt={alumni.name} className="w-28 h-28 rounded-full object-cover ring-4 ring-white shadow-lg" />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-4xl font-bold ring-4 ring-white shadow-lg">
                {alumni.name?.charAt(0)}
              </div>
            )}
            <div className="text-center md:text-left flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{alumni.name}</h1>
              {(alumni.job || alumni.institution) && (
                <p className="text-gray-500 mt-1">
                  {alumni.job}{alumni.job && alumni.institution ? ` ${t('detail.at')} ` : ''}{alumni.institution}
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                {alumni.tahun_pimnas && (
                  <span className="bg-gold-500/10 text-gold-600 text-sm font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                    <FaTrophy /> PIMNAS ke-{alumni.tahun_pimnas}
                  </span>
                )}
                {edu.s1_kampus && (
                  <span className="bg-primary-50 text-primary-700 text-sm font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <FaGraduationCap /> S1: {edu.s1_kampus}
                  </span>
                )}
                {(edu.s2_kampus || edu.s2_jurusan) && (
                  <span className="bg-blue-50 text-blue-700 text-sm font-medium px-3 py-1 rounded-full">
                    S2: {edu.s2_kampus || edu.s2_jurusan}
                  </span>
                )}
                {(edu.s3_kampus || edu.s3_jurusan) && (
                  <span className="bg-purple-50 text-purple-700 text-sm font-medium px-3 py-1 rounded-full">
                    S3: {edu.s3_kampus || edu.s3_jurusan}
                  </span>
                )}
                {alumni.job && (
                  <span className="bg-amber-50 text-amber-700 text-sm font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <FaBriefcase /> {alumni.job}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => generateAlumniCV(alumni)}
                className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-4 py-2 rounded-lg text-sm font-medium transition"
              >
                <FaFilePdf /> {t('detail.downloadCV')}
              </button>
              {!isAdmin && !hasPendingEdit && (
                <Link to={`/alumni/${alumni.id}/request-edit`} className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 hover:bg-amber-100 px-4 py-2 rounded-lg text-sm font-medium transition">
                  <FaPen /> {t('detail.requestEdit')}
                </Link>
              )}
              {!isAdmin && hasPendingEdit && (
                <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-4 py-2 rounded-lg text-sm font-medium cursor-not-allowed">
                  ⏳ {t('detail.editPending')}
                </span>
              )}
              {isAdmin && (
                <>
                  <Link to={`/edit-alumni/${alumni.id}`} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 hover:bg-primary-100 px-4 py-2 rounded-lg text-sm font-medium transition">
                    <FaEdit /> {t('detail.edit')}
                  </Link>
                  <button onClick={handleDelete} className="inline-flex items-center gap-1 bg-red-50 text-red-600 hover:bg-red-100 px-4 py-2 rounded-lg text-sm font-medium transition">
                    <FaTrash /> {t('detail.delete')}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('detail.email')}</h3>
                {alumni.email ? (
                  <a href={`mailto:${alumni.email}`} className="text-gray-800 hover:text-primary-600 flex items-center gap-2 transition-colors">
                    <FaEnvelope className="text-primary-500" /> {alumni.email}
                  </a>
                ) : <span className="text-gray-400 text-sm">{t('detail.notFilled')}</span>}
              </div>

              {/* Pendidikan */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">{t('detail.education')}</h3>
                <div className="space-y-2">
                  {(edu.s1_kampus || edu.s1_jurusan) && (
                    <p className="text-gray-800 flex items-start gap-2">
                      <FaGraduationCap className="text-primary-500 mt-0.5 flex-shrink-0" />
                      <span>
                        <span className="font-medium">S1:</span> {eduLine(edu.s1_kampus, edu.s1_jurusan)}
                        <span className="text-xs bg-gold-500/10 text-gold-600 px-2 py-0.5 rounded-full ml-2">{t('detail.almamaterPimnas')}</span>
                      </span>
                    </p>
                  )}
                  {(edu.s2_kampus || edu.s2_jurusan) && (
                    <p className="text-gray-800 flex items-start gap-2 pl-6">
                      <span><span className="font-medium">S2:</span> {eduLine(edu.s2_kampus, edu.s2_jurusan)}</span>
                    </p>
                  )}
                  {(edu.s3_kampus || edu.s3_jurusan) && (
                    <p className="text-gray-800 flex items-start gap-2 pl-6">
                      <span><span className="font-medium">S3:</span> {eduLine(edu.s3_kampus, edu.s3_jurusan)}</span>
                    </p>
                  )}
                  {!edu.s1_kampus && !edu.s1_jurusan && !edu.s2_kampus && !edu.s3_kampus && (
                    <span className="text-gray-400 text-sm">{t('detail.notFilled')}</span>
                  )}
                </div>
              </div>

              {/* Pekerjaan & Instansi */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('detail.career')}</h3>
                {alumni.job ? (
                  <p className="text-gray-800 flex items-center gap-2">
                    <FaBriefcase className="text-primary-500" /> {alumni.job}
                  </p>
                ) : <span className="text-gray-400 text-sm">{t('detail.notFilled')}</span>}
                {alumni.institution && (
                  <p className="text-gray-800 flex items-center gap-2 mt-2">
                    <FaBuilding className="text-primary-500" /> {alumni.institution}
                  </p>
                )}
                {alumni.domisili && (
                  <p className="text-gray-800 flex items-center gap-2 mt-2">
                    <FaMapMarkerAlt className="text-primary-500" /> {t('detail.domicile')}: {alumni.domisili}
                  </p>
                )}
              </div>
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('detail.onlineProfile')}</h3>
                <div className="flex items-center gap-4">
                  {alumni.linkedin ? (
                    <a href={alumni.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors">
                      <FaLinkedin className="text-xl" /> LinkedIn
                    </a>
                  ) : <span className="text-gray-400 text-sm">—</span>}
                  {alumni.google_scholar && (
                    <a href={alumni.google_scholar} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-red-500 hover:text-red-600 font-medium transition-colors">
                      <FaGoogle className="text-xl" /> Scholar
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Minat Riset */}
            {alumni.research_interests && (
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <FaFlask className="text-primary-500" /> {t('detail.researchInterests')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {alumni.research_interests.split(',').map((interest, i) => (
                    <span key={i} className="bg-primary-50 text-primary-700 text-sm font-medium px-3 py-1.5 rounded-full">
                      {interest.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Back link */}
        <div className="mt-6 mb-12">
          <Link to="/alumni" className="text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-2 transition-colors">
            <FaArrowLeft /> {t('detail.backToDirectory')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AlumniDetail;
